import React, { forwardRef, createElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ExternalLink } from 'api/contentful/contentTypes.cloverdotcom';
import { TrackedLink } from 'components/TrackedLink/TrackedLink';
import { VisuallyHidden } from 'components/VisuallyHidden/VisuallyHidden';
import type { FlatEntry } from 'services/cms/flattenContentful';
import { isChildrenDefined } from 'utils/ChildrenCheck';
import { getUrlWithParamsOrPdpRedirect, shouldRedirectExistingMerchant } from 'utils/ExistingMerchant';
import { getEntryDataAttributes } from 'utils/GetEntryDataAttributes';
import { testAttributeWithPrefix } from 'utils/DataTestId';
import type { IContentLinkV2Props } from './ContentLinkV2';
import { BooleanFilter } from 'utils/TypeUtils';
import { useReseller } from 'hooks/Reseller/useReseller';

interface IExternalLinkV2Props extends IContentLinkV2Props<FlatEntry<ExternalLink>> {
  titleTag?: string;
  dataTestIdPrefix?: string;
}

export const ExternalLinkV2 = forwardRef<HTMLAnchorElement, IExternalLinkV2Props>(
  ({ entry, target, rel, children, hideTitle, titleTag = 'span', dataTestIdPrefix, ...props }, ref) => {
    const { resellerTemplate } = useReseller();
    const { t } = useTranslation();

    if (!entry) return null;

    const { newTab, hint } = entry;
    let { title: linkText } = entry;

    let href = getUrlWithParamsOrPdpRedirect(entry);
    const isPhoneNumber = (href = '') => href.includes('tel:');

    const shouldRedirectToPDP = shouldRedirectExistingMerchant(entry);

    if (resellerTemplate.resellerPhoneInfo?.phoneNumberLink && isPhoneNumber(href)) {
      linkText = resellerTemplate.resellerPhoneInfo.phoneNumberLink.linkText;
      href = resellerTemplate.resellerPhoneInfo.phoneNumberLink.url;
    }

    const dataTestId = testAttributeWithPrefix('external-link', linkText, dataTestIdPrefix);

    const childrenIsValid = isChildrenDefined(children);
    const contactSalesLabel =
      shouldRedirectToPDP &&
      !childrenIsValid &&
      createElement(titleTag, { 'data-testid': 'text' }, t('contact-sales'));
    const titleLabel = !contactSalesLabel && !hideTitle && linkText && createElement(titleTag, '', linkText);
    const ariaLabel = (!titleLabel && !contactSalesLabel && !childrenIsValid && linkText) || undefined;

    return (
      <TrackedLink
        ref={ref}
        href={href || undefined}
        target={target || (newTab && '_blank') || undefined}
        aria-label={ariaLabel}
        rel={[newTab && 'noopener', rel].filter(BooleanFilter).join(' ') || undefined}
        data-testid={dataTestId}
        {...getEntryDataAttributes(entry)}
        {...props}
      >
        {contactSalesLabel}
        {titleLabel}
        {childrenIsValid && children}
        <VisuallyHidden>{hint}</VisuallyHidden>
      </TrackedLink>
    );
  },
);

ExternalLinkV2.displayName = 'ExternalLinkV2';
